import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { Carousel as RSCarousel, CarouselItem } from 'reactstrap'
import CarouselControl from './CarouselControl'
import CarouselIndicators from './CarouselIndicators'

export const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']

const color = PropTypes.color
const Icon = PropTypes.Icon

export const theme = {
  indicatorColor: color,
  indicatorActiveColor: color,
  indicatorIcon: Icon,
  iconSize: PropTypes.oneOf(ICON_SIZE),
  indicatorActiveIcon: Icon,
  directionArrowColor: color,
  directionArrowLeftIcon: Icon,
  directionArrowRightIcon: Icon
}

export default class Carousel extends Component {
  static propTypes = {
    /**
   * Children components that will be rendered inside Carousel
   */
    children: PropTypes.arrayOf(PropTypes.node),

    /**
     * Display time of each slide in ms (default: 2000ms)
     */
    interval: PropTypes.number,

    /**
     * Slide number to start from
     */
    startAt: PropTypes.number,

    /**
     * Maximum number of items to show
     */
    maxItems: PropTypes.number,

    /**
     * Slider - The component will have a start and an end, and the user can scroll from left to right, and back. When the user hits the end, either left or right, they will have to go back.
     * Carousel - The component will start at the beginning when the user hits the end, in a seamless fashion, like a carousel.
     */
    type: PropTypes.oneOf(['slider', 'carousel']),

    /**
     * Enable / Disable autoplay on load
     */
    autoplay: PropTypes.bool,

    /**
     * Enable / Disable pause of Auto-change when the mouse hovers over the component
     */
    hoverPause: PropTypes.bool,

    /**
     * Enable / Disable if the left and right arrow keys should control the carousel
     */
    keyboard: PropTypes.bool,

    /**
     * If enabled custom control arrows can be displayed instead of the one that are built-in
     */
    useCustomArrows: PropTypes.bool,

    /**
     * Enable / Disable page indicators
     */
    indicators: PropTypes.bool,

    /**
     * Function that will be triggered when clicking "Next" arrow key
     */
    onNextClick: PropTypes.func,

    /**
     * Function that will be triggered when clicking "Previous" arrow key
     */
    onPrevClick: PropTypes.func,

    /**
     * Adjust the colors, borders, and alignment
     */
    styling: PropTypes.shape(theme)

  }

  static defaultProps = {
    indicators: true,
    type: 'carousel',
    autoplay: true,
    interval: 2000,
    hoverPause: true,
    keyboard: false,
    useCustomArrows: true,
    styling: {
      iconSize: '2x'
    }
  }

  constructor (props) {
    super(props)
    this.state = {
      activeIndex: 0,
      currentSlide: 'first'
    }
    this.next = this.next.bind(this)
    this.previous = this.previous.bind(this)
    this.goToIndex = this.goToIndex.bind(this)
    this.onExiting = this.onExiting.bind(this)
    this.onExited = this.onExited.bind(this)
  }
  onExiting () {
    this.animating = true
  }

  onExited () {
    this.animating = false
  }

  next (currentSlideIndex) {
    const { onNextClick } = this.props
    onNextClick && onNextClick(currentSlideIndex + 1)
    const { activeIndex } = this.state
    const { children } = this.props
    if (this.animating) return
    const nextIndex = activeIndex === children.length - 1 ? 0 : activeIndex + 1
    this.setState({
      activeIndex: nextIndex,
      currentSlide: nextIndex === children.length - 1 ? 'last' : nextIndex
    })
  }

  previous (currentSlideIndex) {
    const { onPrevClick } = this.props
    onPrevClick && onPrevClick(currentSlideIndex - 1)
    const { activeIndex } = this.state
    const { children } = this.props
    if (this.animating) return
    const nextIndex = activeIndex === 0 ? children.length - 1 : activeIndex - 1
    this.setState({
      activeIndex: nextIndex,
      currentSlide: nextIndex === 0 ? 'first' : nextIndex
    })
  }

  goToIndex (newIndex) {
    if (this.animating) return
    this.setState({
      activeIndex: newIndex
    })
  }

  render () {
    let children = Array.isArray(this.props.children) ? this.props.children : [this.props.children]
    const { activeIndex, currentSlide } = this.state
    const {
      type,
      indicators,
      hoverPause,
      interval,
      autoplay,
      keyboard,
      useCustomArrows,
      maxItems,
      styling: {
        indicatorColor,
        indicatorActiveColor,
        indicatorIcon,
        indicatorActiveIcon,
        directionArrowColor,
        directionArrowLeftIcon,
        directionArrowRightIcon,
        iconSize
      } = {}
    } = this.props

    if (maxItems > 0) children = children.slice(0, maxItems)

    const pause = hoverPause ? 'hover' : false
    const slideInterval = type === 'slider' ? 0 : interval
    const ride = autoplay && 'carousel'

    const slides = children && children.map((child, index) => {
      return (
        <CarouselItem
          key={index}
          onExiting={this.onExiting}
          onExited={this.onExited}
        >
          {child}
        </CarouselItem>
      )
    })
    return (
      <div className='polystackCarousel'>
        <RSCarousel
          activeIndex={activeIndex}
          next={this.next}
          previous={this.previous}
          pause={pause}
          interval={slideInterval}
          ride={ride}
          keyboard={keyboard}
          className='polystackCarousel'
        >
          {indicators && <CarouselIndicators items={slides} activeIndex={activeIndex} onClickHandler={this.goToIndex} activeIcon={indicatorActiveIcon} activeColor={indicatorActiveColor} color={indicatorColor} icon={indicatorIcon} />}
          {slides}
          {
            !(type === 'slider' && currentSlide === 'first') &&
            <CarouselControl
              direction='prev'
              directionText='Previous'
              onClickHandler={() => this.previous(activeIndex)}
              icon={directionArrowLeftIcon}
              iconSize={iconSize}
              color={directionArrowColor}
              useCustomArrows={useCustomArrows}
            />
          }
          {
            !(type === 'slider' && currentSlide === 'last') &&
            <CarouselControl
              direction='next'
              directionText='Next'
              onClickHandler={() => this.next(activeIndex)}
              icon={directionArrowRightIcon}
              iconSize={iconSize}
              color={directionArrowColor}
              useCustomArrows={useCustomArrows}
            />
          }
        </RSCarousel>
      </div>
    )
  }
}
